import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  TimePicker,
  message
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { fetchHealthDoctors, fetchSprTov } from "../../health/clients/health-clients-api";
import {
  addSprZalGrafUsl,
  createSprZalGrafSession,
  deleteSprZalGrafSession,
  deleteSprZalGrafUsl,
  fetchSprZalGrafSession,
  fetchSprZalGrafUsl,
  replaceSprZalGrafUsl,
  saveSprZalGrafSession
} from "./spr-zal-api";
import "./spr-zal-graf-session-drawer.css";

const DRAWER_WIDTH_KEY = "planOnline.sprZal.grafSessionDrawer.width";
const MIN_WIDTH = 420;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 480;

const LABEL_WIDTH = 180;

const formItemLayout = {
  labelCol: { flex: `0 0 ${LABEL_WIDTH}px` },
  wrapperCol: { flex: "1 1 auto" },
  labelAlign: "right"
};

const clampWidth = (value) => {
  const max = typeof window === "undefined" ? MAX_WIDTH : Math.min(MAX_WIDTH, window.innerWidth);
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_WIDTH;
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toTime = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

/** Локальное время без Z — иначе UTC-сдвиг ломает TIME_BEG/TIME_END на сервере. */
const timeToLocalIso = (date, time) => {
  if (!date?.isValid?.() || !time?.isValid?.()) return null;
  return date
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0)
    .format("YYYY-MM-DDTHH:mm:ss");
};

const normalizeTov = (row) => {
  const idTov = toNumber(pick(row, "idTov", "IdTov", "idUsl", "IdUsl"));
  const name = pick(row, "name", "Name") || "";
  const art = pick(row, "art", "Art") || "";
  return {
    value: idTov,
    label: art ? `${name} (${art})` : name || `#${idTov}`,
    name,
    art
  };
};

const normalizeUsl = (row) => {
  const idUsl = toNumber(pick(row, "idUsl", "IdUsl"));
  const name = pick(row, "name", "Name") || "";
  const art = pick(row, "art", "Art") || "";
  return {
    idUsl,
    name,
    art,
    label: art ? `${name} (${art})` : name || `#${idUsl}`
  };
};

/**
 * Drawer сеанса графика (Delphi TFrmEPlanZal + услуги SPR_ZAL_GRAF_USL).
 * mode: "new" | "edit"
 * defaults: { fdate, timeBeg, timeEnd } для new
 */
const SprZalGrafSessionDrawer = ({
  open,
  idZal,
  zalName,
  idStr,
  isNew = false,
  defaults,
  onClose,
  onSaved,
  onDeleted
}) => {
  const [size, setSize] = useState(loadWidth);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [cntPlan, setCntPlan] = useState(0);
  const [sessionIdStr, setSessionIdStr] = useState(null);
  const [uslList, setUslList] = useState([]);
  const [uslLoading, setUslLoading] = useState(false);
  const [uslBusy, setUslBusy] = useState(false);
  const [tovOptions, setTovOptions] = useState([]);
  const [tovLoading, setTovLoading] = useState(false);
  const [addUslId, setAddUslId] = useState(undefined);
  const tovSearchRef = useRef(0);
  const tovTimerRef = useRef(0);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const doctorOptions = useMemo(
    () => [
      { value: 0, label: "—" },
      ...(doctors || [])
        .map((d) => ({
          value: toNumber(pick(d, "idSotr", "IdSotr")),
          label: pick(d, "name", "Name") || ""
        }))
        .filter((o) => o.value != null)
    ],
    [doctors]
  );

  const selectedUslIds = useMemo(
    () => new Set(uslList.map((u) => u.idUsl).filter((id) => id != null)),
    [uslList]
  );

  const loadTov = useCallback((q) => {
    clearTimeout(tovTimerRef.current);
    tovTimerRef.current = setTimeout(() => {
      const requestId = ++tovSearchRef.current;
      setTovLoading(true);
      fetchSprTov(q)
        .then((data) => {
          if (requestId !== tovSearchRef.current) return;
          setTovOptions(Array.isArray(data) ? data.map(normalizeTov).filter((o) => o.value != null) : []);
        })
        .catch(() => {
          if (requestId !== tovSearchRef.current) return;
          setTovOptions([]);
        })
        .finally(() => {
          if (requestId === tovSearchRef.current) setTovLoading(false);
        });
    }, 300);
  }, []);

  const loadUsl = useCallback(
    (zalId, strId) => {
      if (zalId == null || strId == null) {
        setUslList([]);
        return Promise.resolve();
      }
      setUslLoading(true);
      return fetchSprZalGrafUsl(zalId, strId)
        .then((data) => {
          setUslList(Array.isArray(data) ? data.map(normalizeUsl).filter((u) => u.idUsl != null) : []);
        })
        .catch((e) => {
          message.error(e.message || "Не удалось загрузить услуги");
          setUslList([]);
        })
        .finally(() => setUslLoading(false));
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchHealthDoctors()
      .then((data) => {
        if (!cancelled) setDoctors(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setDoctors([]);
      });
    loadTov("");
    return () => {
      cancelled = true;
    };
  }, [open, loadTov]);

  useEffect(() => {
    if (!open || idZal == null) return;
    let cancelled = false;
    setError("");
    setAddUslId(undefined);

    if (isNew) {
      setSessionIdStr(null);
      setUslList([]);
      setCntPlan(0);
      setLoading(false);
      const fdate = defaults?.fdate ? dayjs(defaults.fdate) : dayjs();
      form.setFieldsValue({
        idStr: null,
        fdate: fdate.isValid() ? fdate : dayjs(),
        timeBeg: toTime(defaults?.timeBeg) || fdate.hour(9).minute(0),
        timeEnd: toTime(defaults?.timeEnd) || fdate.hour(9).minute(30),
        cntMax: toNumber(defaults?.cntMax) ?? 1,
        idSotr: toNumber(defaults?.idSotr) || 0,
        idSotrRun: toNumber(defaults?.idSotrRun) || 0
      });
      return () => {
        cancelled = true;
      };
    }

    if (idStr == null) return undefined;

    setSessionIdStr(idStr);
    setLoading(true);
    fetchSprZalGrafSession(idZal, idStr)
      .then((row) => {
        if (cancelled) return;
        const fdate = dayjs(pick(row, "fdate", "Fdate"));
        setCntPlan(toNumber(pick(row, "cntPlan", "CntPlan")) ?? 0);
        form.setFieldsValue({
          idStr: toNumber(pick(row, "idStr", "IdStr")),
          fdate: fdate.isValid() ? fdate : dayjs(),
          timeBeg: toTime(pick(row, "timeBeg", "TimeBeg")),
          timeEnd: toTime(pick(row, "timeEnd", "TimeEnd")),
          cntMax: toNumber(pick(row, "cntMax", "CntMax")) ?? 1,
          idSotr: toNumber(pick(row, "idSotr", "IdSotr")) || 0,
          idSotrRun: toNumber(pick(row, "idSotrRun", "IdSotrRun")) || 0
        });
        return loadUsl(idZal, idStr);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить сеанс");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isNew, idZal, idStr, defaults, form, loadUsl]);

  const onFinish = async (values) => {
    setSaving(true);
    setError("");
    const body = {
      fdate: values.fdate.format("YYYY-MM-DD"),
      timeBeg: timeToLocalIso(values.fdate, values.timeBeg),
      timeEnd: timeToLocalIso(values.fdate, values.timeEnd),
      cntMax: values.cntMax ?? 1,
      idSotr: values.idSotr || 0,
      idSotrRun: values.idSotrRun || 0
    };

    try {
      if (sessionIdStr == null) {
        const created = await createSprZalGrafSession(idZal, body);
        const newId = toNumber(pick(created, "idStr", "IdStr"));
        const uslIds = Array.isArray(defaults?.uslIds)
          ? defaults.uslIds.map((id) => toNumber(id)).filter((id) => id > 0)
          : [];
        const fromPaste = Array.isArray(defaults?.uslIds);
        if (newId != null && uslIds.length) {
          for (const idUsl of uslIds) {
            try {
              await addSprZalGrafUsl(idZal, newId, idUsl);
            } catch {
              /* дубликаты / ошибки отдельных услуг не блокируют сеанс */
            }
          }
        }
        message.success("Сеанс добавлен");
        onSaved?.(created);
        if (fromPaste) {
          onClose?.();
        } else {
          setSessionIdStr(newId);
          form.setFieldsValue({ idStr: newId });
          if (newId != null) await loadUsl(idZal, newId);
        }
      } else {
        const saved = await saveSprZalGrafSession(idZal, sessionIdStr, body);
        message.success("Сеанс сохранён");
        onSaved?.(saved);
      }
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const optionsForRow = useCallback(
    (currentId) => {
      const fromSearch = tovOptions.filter(
        (o) => o.value === currentId || !selectedUslIds.has(o.value)
      );
      if (currentId != null && !fromSearch.some((o) => o.value === currentId)) {
        const current = uslList.find((u) => u.idUsl === currentId);
        if (current) {
          fromSearch.unshift({ value: current.idUsl, label: current.label });
        }
      }
      return fromSearch;
    },
    [tovOptions, selectedUslIds, uslList]
  );

  const addOptions = useMemo(
    () => tovOptions.filter((o) => !selectedUslIds.has(o.value)),
    [tovOptions, selectedUslIds]
  );

  const onAddUsl = async (idUsl) => {
    if (sessionIdStr == null || idUsl == null) return;
    if (selectedUslIds.has(idUsl)) {
      message.warning("Эта услуга уже добавлена");
      setAddUslId(undefined);
      return;
    }
    setUslBusy(true);
    try {
      await addSprZalGrafUsl(idZal, sessionIdStr, idUsl);
      setAddUslId(undefined);
      await loadUsl(idZal, sessionIdStr);
      onSaved?.();
    } catch (e) {
      message.error(e.message || "Не удалось добавить услугу");
    } finally {
      setUslBusy(false);
    }
  };

  const onChangeUsl = async (oldIdUsl, newIdUsl) => {
    if (sessionIdStr == null || newIdUsl == null || oldIdUsl === newIdUsl) return;
    if (selectedUslIds.has(newIdUsl)) {
      message.warning("Эта услуга уже добавлена");
      return;
    }
    setUslBusy(true);
    try {
      await replaceSprZalGrafUsl(idZal, sessionIdStr, oldIdUsl, newIdUsl);
      await loadUsl(idZal, sessionIdStr);
      onSaved?.();
    } catch (e) {
      message.error(e.message || "Не удалось изменить услугу");
    } finally {
      setUslBusy(false);
    }
  };

  const onDeleteUsl = async (idUsl) => {
    if (sessionIdStr == null || idUsl == null) return;
    setUslBusy(true);
    try {
      await deleteSprZalGrafUsl(idZal, sessionIdStr, idUsl);
      await loadUsl(idZal, sessionIdStr);
      onSaved?.();
    } catch (e) {
      message.error(e.message || "Не удалось удалить услугу");
    } finally {
      setUslBusy(false);
    }
  };

  const onDeleteSession = () => {
    if (idZal == null || sessionIdStr == null) return;
    Modal.confirm({
      title: "Удалить сеанс?",
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: () =>
        deleteSprZalGrafSession(idZal, sessionIdStr)
          .then(() => {
            message.success("Сеанс удалён");
            onDeleted?.(sessionIdStr);
            onClose?.();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
            return Promise.reject(e);
          })
    });
  };

  const uslEnabled = sessionIdStr != null;

  const zalOptions = useMemo(() => {
    if (idZal == null) return [];
    return [{ value: idZal, label: zalName || `#${idZal}` }];
  }, [idZal, zalName]);

  return (
    <Drawer
      placement="right"
      title={sessionIdStr == null ? "Новый сеанс" : "Сеанс графика"}
      size={size}
      maxSize={MAX_WIDTH}
      open={open}
      onClose={onClose}
      destroyOnClose
      rootClassName="spr-zal-graf-session-drawer"
      styles={{
        body: { display: "flex", flexDirection: "column", paddingTop: 12 }
      }}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {loading ? (
        <div className="spr-zal-graf-session__center">
          <Spin tip="Загрузка..." />
        </div>
      ) : (
        <div className="spr-zal-graf-session">
          <div className="spr-zal-graf-session__body">
            {error ? (
              <Alert className="spr-zal-graf-session__error" type="error" showIcon message={error} />
            ) : null}
            <Form
              form={form}
              className="spr-zal-graf-session__form"
              {...formItemLayout}
              colon={false}
              onFinish={onFinish}
              disabled={saving}
            >
              <Form.Item label="Кабинет">
                <Select options={zalOptions} value={idZal} disabled style={{ width: "100%" }} />
              </Form.Item>
              {sessionIdStr != null ? (
                <Form.Item label="№" name="idStr">
                  <InputNumber disabled style={{ width: "100%" }} />
                </Form.Item>
              ) : null}
              <Form.Item label="Дата" name="fdate" rules={[{ required: true, message: "Укажите дату" }]}>
                <DatePicker
                  format="DD.MM.YYYY"
                  style={{ width: "100%" }}
                  allowClear={false}
                  disabled={sessionIdStr != null}
                />
              </Form.Item>
              <Form.Item
                label="Начало периода"
                name="timeBeg"
                rules={[{ required: true, message: "Укажите начало" }]}
              >
                <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear={false} />
              </Form.Item>
              <Form.Item
                label="Окончание периода"
                name="timeEnd"
                rules={[{ required: true, message: "Укажите окончание" }]}
              >
                <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear={false} />
              </Form.Item>
              <Form.Item label="Макс. кол-во участников" name="cntMax">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
              {sessionIdStr != null && cntPlan > 0 ? (
                <div className="spr-zal-graf-session__hint">Запланировано клиентов: {cntPlan}</div>
              ) : null}
              <Form.Item label="Только для сотрудника" name="idSotr">
                <Select options={doctorOptions} showSearch optionFilterProp="label" />
              </Form.Item>
              <Form.Item label="Исполнитель" name="idSotrRun">
                <Select options={doctorOptions} showSearch optionFilterProp="label" />
              </Form.Item>
              <Form.Item label=" " colon={false}>
                <Space>
                  <Button type="primary" htmlType="submit" loading={saving}>
                    Сохранить
                  </Button>
                  {sessionIdStr != null ? (
                    <Button danger onClick={onDeleteSession} disabled={saving}>
                      Удалить
                    </Button>
                  ) : null}
                </Space>
              </Form.Item>
            </Form>

            <div className="spr-zal-graf-session__usl">
              <div className="spr-zal-graf-session__usl-title">Услуги</div>
              {!uslEnabled ? (
                <div className="spr-zal-graf-session__usl-hint">
                  Сначала сохраните сеанс, затем можно добавить услуги
                </div>
              ) : (
                <Spin spinning={uslLoading || uslBusy}>
                  <div className="spr-zal-graf-session__usl-list">
                    {uslList.map((usl) => (
                      <Space.Compact key={usl.idUsl} block className="spr-zal-graf-session__usl-row">
                        <Select
                          showSearch
                          optionFilterProp="label"
                          filterOption={false}
                          onSearch={loadTov}
                          options={optionsForRow(usl.idUsl)}
                          value={usl.idUsl}
                          onChange={(v) => onChangeUsl(usl.idUsl, v)}
                          loading={tovLoading}
                          style={{ width: "100%" }}
                          disabled={saving}
                        />
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          title="Удалить услугу"
                          aria-label="Удалить услугу"
                          onClick={() => onDeleteUsl(usl.idUsl)}
                          disabled={saving}
                        />
                      </Space.Compact>
                    ))}
                    <Select
                      showSearch
                      allowClear
                      placeholder="Добавить услугу..."
                      optionFilterProp="label"
                      filterOption={false}
                      onSearch={loadTov}
                      options={addOptions}
                      value={addUslId}
                      onChange={(v) => {
                        setAddUslId(v);
                        if (v != null) onAddUsl(v);
                      }}
                      loading={tovLoading}
                      style={{ width: "100%" }}
                      disabled={saving}
                    />
                  </div>
                </Spin>
              )}
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default SprZalGrafSessionDrawer;
